import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SellFromHome from '../../../assets/images/sell-from-home.png';
import { ReactComponent as ArrowRight } from '../../../assets/images/svg/arrow-right-blue.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import BottomDrawer from '../../Drawer/Drawer';
import Button from '../../Button/Button';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { authScopeStringGetHelper } from '../../../util/helpers/authScopeHelpers';
import { JWT_TOKEN } from '../../../constants/localStorage';
import {
  getCurrentAccountCode,
  getCurrentAccountUid,
  selectNagDashboard,
} from '../../../store/selectors/accountSelectors';
import { UPGRADE_STATUS_TYPES } from '../../../constants/nagDashboardContants';

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  background: ${themeColors.colorBannerBg};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  margin: ${pxToRem(36)} ${pxToRem(-16)} 0;
  cursor: pointer;
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BannerTitle = styled.span`
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  font-size: ${pxToRem(16)};
`;

const BannerSubtitle = styled.span`
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  font-size: ${pxToRem(14)};
`;

const BannerCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(6)};
`;

const BannerCtaLabel = styled.span`
  font-weight: 400;
  color: ${themeColors.colorPrimary};
  font-size: ${pxToRem(14)};
  text-decoration: underline;
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${pxToRem(8)} 0;
`;

const DrawerIconWrapper = styled.div`
  margin-bottom: ${pxToRem(16)};
`;

const DrawerTitle = styled.h3`
  font-size: 16;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin-bottom: ${pxToRem(8)};
`;

const DrawerDescription = styled.p`
  font-size: 14;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  line-height: 1.5;
`;

const DrawerFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};

  > button {
    width: 100%;
  }
`;

const SellFromHomeBanner = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { RegistrationUrlV2: registrationApi } = useSelector(selectSettings);
  const token = authScopeStringGetHelper(JWT_TOKEN);
  const accountUid = useSelector(getCurrentAccountUid);
  const accountCode = useSelector(getCurrentAccountCode);
  const nagDashboard = useSelector(selectNagDashboard);
  const isUpgradeProgress =
    nagDashboard?.UpgradeStatusType === UPGRADE_STATUS_TYPES.InProgress;

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const goToRegistrationPage = () => {
    window.location.replace(
      `${registrationApi}/choose-application?accountCode=${accountCode}&accountUid=${accountUid}&token=${token}`,
    );
  };

  const drawerFooter = (
    <DrawerFooter>
      <Button
        variant="primary"
        textTransform="none"
        onClick={goToRegistrationPage}
      >
        {isUpgradeProgress ? t('common.continue') : t('common.verifyAccount')}
      </Button>
      <Button
        variant="primaryOutlined"
        textTransform="none"
        onClick={handleClose}
      >
        {t('common.doItLater')}
      </Button>
    </DrawerFooter>
  );

  return (
    <>
      <BannerContainer onClick={handleClick}>
        <BannerLeft>
          <img src={SellFromHome} alt="Sell From Home" width={55} height={35} />
          <BannerText>
            <BannerTitle>{t('sellWizard.sellFromHome.title')}</BannerTitle>
            <BannerSubtitle>
              {t('sellWizard.sellFromHome.subtitle')}
            </BannerSubtitle>
          </BannerText>
        </BannerLeft>
        <BannerCta>
          <BannerCtaLabel>{t('sellWizard.sellFromHome.cta')}</BannerCtaLabel>
          <ArrowRight fill="#052D6B" />
        </BannerCta>
      </BannerContainer>

      <BottomDrawer
        isOpen={open}
        onClose={handleClose}
        footer={drawerFooter}
        maxHeight="70%"
      >
        <DrawerContent>
          <DrawerIconWrapper>
            <InfoIcon width={35} height={35} />
          </DrawerIconWrapper>
          <DrawerTitle>
            {t('basicAccountRestrictions.verifyAccountSellFromHome.title')}
          </DrawerTitle>
          <DrawerDescription>
            {t(
              'basicAccountRestrictions.verifyAccountSellFromHome.description',
            )}
          </DrawerDescription>
        </DrawerContent>
      </BottomDrawer>
    </>
  );
};

export default SellFromHomeBanner;
